import React from 'react';

import PaginationControls from '../components/controls/PaginationControls';

export type PaginatedRecordState = {
  // False for records belonging to a page other than the active one.
  isVisible: boolean;
  // True for the last record of the active page, so callers can keep separator styling identical
  // to when only the active page was rendered.
  isLastVisible: boolean;
};

type PaginatedContentRendererProps<T> = {
  data: T[];
  itemsPerPage: number;
  activePage: number;
  setActivePage: (page: number) => void;
  children: (record: T, state: PaginatedRecordState) => React.ReactNode;
};

/**
 * Every record is rendered into the markup and the inactive pages are hidden with CSS, rather than
 * being sliced away before rendering.
 *
 * Slicing meant the server HTML only ever held the first page. On /blog that left every post
 * beyond page one with no crawlable link anywhere on the site: search engines reached them from
 * the sitemap, found nothing linking to them, and parked them as "crawled, currently not indexed".
 * The pagination controls are buttons rather than links, so there is no second URL a crawler could
 * follow to find the rest either.
 */
const PaginatedContentRenderer = <T extends {}>(props: PaginatedContentRendererProps<T>) => {
  const { data, itemsPerPage, activePage, setActivePage, children } = props;
  const totalCount = data.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const firstVisibleIndex = activePage * itemsPerPage;
  const lastVisibleIndex = Math.min(firstVisibleIndex + itemsPerPage, totalCount) - 1;
  return (
    <div>
      {data.map((record, index) =>
        children(record, {
          isVisible: index >= firstVisibleIndex && index <= lastVisibleIndex,
          isLastVisible: index === lastVisibleIndex,
        }),
      )}
      <div className="flex flex-col gap-2 items-center mt-4">
        {totalPages > 0 && <PaginationControls activePage={activePage} setActivePage={setActivePage} totalPages={totalPages} />}
      </div>
    </div>
  );
};
PaginatedContentRenderer.displayName = 'PaginatedContentRenderer';
export default PaginatedContentRenderer;
